import React, { useState } from 'react';
import {
  Chip,
  Menu,
  MenuItem,
  CircularProgress,
  Typography,
} from '@mui/material';
import { CheckCircle } from '@mui/icons-material';
import { jobsApi } from '../services/api';

interface JobStatusChipProps {
  jobId: number;
  currentStatus: string;
  onStatusChange?: (newStatus: string) => void;
  canEdit?: boolean;
}

const JobStatusChip: React.FC<JobStatusChipProps> = ({
  jobId,
  currentStatus,
  onStatusChange,
  canEdit = true,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [updating, setUpdating] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(currentStatus);

  const statusOptions = [
    { value: 'open', label: 'Open', color: 'success' },
    { value: 'paused', label: 'Paused', color: 'warning' },
    { value: 'closed', label: 'Closed', color: 'default' },
    { value: 'filled', label: 'Filled', color: 'info' },
  ];

  const getStatusColor = (status: string) => {
    const option = statusOptions.find(opt => opt.value === status);
    return option?.color || 'default';
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (canEdit && !updating) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleStatusChange = async (newStatus: string) => {
    if (newStatus === selectedStatus) {
      handleClose();
      return;
    }

    setUpdating(true);
    try {
      await jobsApi.update(jobId.toString(), { status: newStatus as 'open' | 'paused' | 'closed' | 'filled' });
      setSelectedStatus(newStatus);
      if (onStatusChange) {
        onStatusChange(newStatus);
      }
    } catch (error) {
      console.error('Failed to update job status:', error);
      // Reset to current status on error
      setSelectedStatus(currentStatus);
    } finally {
      setUpdating(false);
      handleClose();
    }
  };

  return (
    <>
      <Chip
        label={updating ? <CircularProgress size={16} /> : selectedStatus}
        size="small"
        color={getStatusColor(selectedStatus) as any}
        clickable={canEdit}
        onClick={handleClick}
        sx={{
          cursor: canEdit ? 'pointer' : 'default',
          ...(canEdit && {
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }),
        }}
      />
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <MenuItem disabled sx={{ opacity: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Change status to:
          </Typography>
        </MenuItem>
        {statusOptions.map((option) => (
          <MenuItem
            key={option.value}
            onClick={() => handleStatusChange(option.value)}
            selected={option.value === selectedStatus}
          >
            <Chip
              label={option.label}
              size="small"
              color={option.color as any}
              sx={{ mr: 1 }}
            />
            {option.value === selectedStatus && (
              <CheckCircle fontSize="small" color="primary" />
            )}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default JobStatusChip;